/**
 * useGeminiLive - React hook for Gemini Live API real-time voice
 *
 * Ported from client/components/people/processingChecklist/AddTaskDialog/useGeminiLive.ts
 *
 * Architecture: Direct client-to-Gemini WebSocket connection
 * - Backend provides ephemeral token (no API key exposure)
 * - Client connects directly to Gemini (lowest latency)
 * - Based on: https://ai.google.dev/gemini-api/docs/live-guide
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { GoogleGenAI, Modality } from "@google/genai";
import GlobalVariables from "../models/globalVariables";

const GEMINI_LIVE_API_URL = GlobalVariables.getAPIURL() + "/api/gemini-live";

// Audio configuration per Gemini Live docs
const AUDIO_CONFIG = {
  inputSampleRate: 16000,
  outputSampleRate: 24000,
  inputMimeType: "audio/pcm;rate=16000",
  outputMimeType: "audio/pcm;rate=24000",
};

export const useGeminiLive = (defaultConfig) => {
  // State
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [userTranscript, setUserTranscript] = useState("");
  const [aiTranscript, setAiTranscript] = useState("");

  // Refs
  const sessionRef = useRef(null);
  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const captureContextRef = useRef(null);
  const processorRef = useRef(null);
  const configRef = useRef(defaultConfig || {});

  // Audio playback refs
  const nextPlayTimeRef = useRef(0);
  const activeSourcesRef = useRef([]);
  const currentTurnIdRef = useRef(0);

  // Update config ref
  useEffect(() => {
    if (defaultConfig) {
      configRef.current = { ...configRef.current, ...defaultConfig };
    }
  }, [defaultConfig]);

  /**
   * Stop all currently playing audio sources
   */
  const stopAllAudio = useCallback(() => {
    activeSourcesRef.current.forEach((source) => {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        // Source may already be stopped
      }
    });
    activeSourcesRef.current = [];
    nextPlayTimeRef.current = 0;
    setIsSpeaking(false);
  }, []);

  /**
   * Get or create AudioContext for playback (24kHz output)
   */
  const getPlaybackContext = useCallback(async () => {
    if (!audioContextRef.current || audioContextRef.current.state === "closed") {
      audioContextRef.current = new AudioContext({ sampleRate: 24000 });
    }

    if (audioContextRef.current.state === "suspended") {
      await audioContextRef.current.resume();
    }

    return audioContextRef.current;
  }, []);

  /**
   * Play audio chunk from Gemini (base64 PCM 24kHz)
   */
  const playAudioChunk = useCallback(
    async (base64Audio) => {
      try {
        const audioContext = await getPlaybackContext();

        // Decode base64 to PCM bytes
        const binaryString = atob(base64Audio);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Convert 16-bit PCM to Float32 for Web Audio
        const int16 = new Int16Array(bytes.buffer);
        const float32 = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) {
          float32[i] = int16[i] / 32768.0;
        }

        // Create audio buffer
        const audioBuffer = audioContext.createBuffer(1, float32.length, 24000);
        audioBuffer.getChannelData(0).set(float32);

        // Schedule playback for smooth audio
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(audioContext.destination);

        // Calculate start time for gapless playback
        const currentTime = audioContext.currentTime;
        const startTime = Math.max(currentTime, nextPlayTimeRef.current);

        source.start(startTime);
        nextPlayTimeRef.current = startTime + audioBuffer.duration;

        // Track active source for cleanup
        activeSourcesRef.current.push(source);

        setIsSpeaking(true);

        source.onended = () => {
          // Remove from active sources
          activeSourcesRef.current = activeSourcesRef.current.filter((s) => s !== source);

          // Check if more audio is queued
          if (
            audioContext.currentTime >= nextPlayTimeRef.current - 0.1 &&
            activeSourcesRef.current.length === 0
          ) {
            setIsSpeaking(false);
          }
        };
      } catch (err) {
        console.error("Error playing audio:", err);
      }
    },
    [getPlaybackContext]
  );

  /**
   * Handle messages from Gemini Live session
   */
  const handleGeminiMessage = useCallback(
    async (message) => {
      console.log("Gemini message:", message);

      // Handle tool calls (function calling)
      if (message.toolCall?.functionCalls) {
        console.log("Gemini: Tool call received", message.toolCall.functionCalls);
        const functionResponses = [];

        for (const fc of message.toolCall.functionCalls) {
          console.log(`Calling function: ${fc.name}`, fc.args);

          try {
            // Call the onToolCall handler
            const result = await configRef.current.onToolCall?.({
              id: fc.id,
              name: fc.name,
              args: fc.args || {},
            });

            // Stringify and cap response size to avoid WebSocket limits
            const responseValue = typeof result === "string"
              ? result
              : JSON.stringify(result || "success");
            functionResponses.push({
              id: fc.id,
              name: fc.name,
              response: { result: responseValue.slice(0, 25000) },
            });
          } catch (err) {
            console.error(`Error executing function ${fc.name}:`, err);
            functionResponses.push({
              id: fc.id,
              name: fc.name,
              response: { error: err.message },
            });
          }
        }

        // Send tool responses back to Gemini
        if (sessionRef.current && functionResponses.length > 0) {
          try {
            await sessionRef.current.sendToolResponse({ functionResponses });
            console.log("Tool responses sent:", functionResponses);
          } catch (err) {
            console.error("Error sending tool response:", err);
          }
        }
        return;
      }

      // Handle interruption - stop all playing audio immediately
      if (message.serverContent?.interrupted) {
        console.log("Gemini: Interrupted - stopping all audio");
        stopAllAudio();
        currentTurnIdRef.current++;
        return;
      }

      // Handle audio from modelTurn.parts
      if (message.serverContent?.modelTurn?.parts) {
        for (const part of message.serverContent.modelTurn.parts) {
          // Audio data in inlineData
          if (part.inlineData?.data) {
            playAudioChunk(part.inlineData.data);
          }
          // Text part — internal thinking/reasoning, not the spoken transcript.
          // Actual spoken text comes via outputTranscription below.
          if (part.text) {
            console.log("Gemini Live: Model turn text:", part.text);
          }
        }
      }

      // Handle input transcription (user speech)
      if (message.serverContent?.inputTranscription?.text) {
        const text = message.serverContent.inputTranscription.text;
        console.log("Gemini Live: User transcription:", text);
        setUserTranscript((prev) => prev + " " + text);
        configRef.current.onTranscript?.({
          sessionId: sessionId || "",
          text,
          type: "user",
        });
      }

      // Handle output transcription (model speech)
      if (message.serverContent?.outputTranscription?.text) {
        const text = message.serverContent.outputTranscription.text;
        console.log("Gemini Live: AI transcription:", text);
        setAiTranscript((prev) => prev + " " + text);
        configRef.current.onTranscript?.({
          sessionId: sessionId || "",
          text,
          type: "model",
        });
      }

      // Handle turn completion
      if (message.serverContent?.turnComplete) {
        configRef.current.onTranscript?.({
          sessionId: sessionId || "",
          type: "turnComplete",
        });
      }
    },
    [playAudioChunk, stopAllAudio, sessionId]
  );

  /**
   * Start a new session with direct Gemini connection
   */
  const startSession = useCallback(
    async (config) => {
      if (isSessionActive || isConnecting) return;

      const mergedConfig = { ...configRef.current, ...config };
      configRef.current = mergedConfig;

      setIsConnecting(true);
      setError(null);
      setUserTranscript("");
      setAiTranscript("");

      try {
        // Step 1: Get ephemeral token from backend
        console.log("Fetching ephemeral token from backend...");
        const tokenResponse = await fetch(`${GEMINI_LIVE_API_URL}/token`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            voice: mergedConfig.voice || "Kore",
            systemInstruction: mergedConfig.systemInstruction,
          }),
        });

        if (!tokenResponse.ok) {
          throw new Error(`Failed to get token: ${tokenResponse.statusText}`);
        }

        const tokenData = await tokenResponse.json();
        if (!tokenData.success) {
          throw new Error(tokenData.error || "Failed to get ephemeral token");
        }

        console.log("Got ephemeral token, connecting to Gemini directly...");
        const newSessionId = `live_${Date.now()}`;
        setSessionId(newSessionId);

        // Step 2: Connect directly to Gemini using the ephemeral token
        // IMPORTANT: Must use v1alpha for ephemeral token support
        const ai = new GoogleGenAI({
          apiKey: tokenData.token,
          httpOptions: { apiVersion: "v1alpha" },
        });

        // Build tools config if provided
        const toolsConfig = mergedConfig.tools?.length
          ? [{ functionDeclarations: mergedConfig.tools }]
          : undefined;

        const session = await ai.live.connect({
          model: tokenData.model,
          config: {
            responseModalities: [Modality.AUDIO],
            systemInstruction: tokenData.systemInstruction,
            // Enable transcription for both input (user speech) and output (AI speech)
            inputAudioTranscription: {},
            outputAudioTranscription: {},
            speechConfig: {
              voiceConfig: {
                prebuiltVoiceConfig: {
                  voiceName: tokenData.voice,
                },
              },
            },
            ...(toolsConfig && { tools: toolsConfig }),
          },
          callbacks: {
            onopen: () => {
              console.log("Gemini Live: Connected directly to Gemini");
            },
            onmessage: handleGeminiMessage,
            onerror: (e) => {
              console.error("Gemini Live: Error:", e);
              setError(e.message || "Connection error");
              configRef.current.onError?.(new Error(e.message || "Connection error"));
            },
            onclose: (e) => {
              console.log("Gemini Live: Connection closed", e?.reason);
              cleanup();
              configRef.current.onSessionClosed?.();
            },
          },
        });

        sessionRef.current = session;
        setIsSessionActive(true);

        // Initialize audio context for playback
        await getPlaybackContext();

        // Send initial greeting to trigger Addy's first response
        // This is a hidden warmup message — not shown in chat
        await session.sendClientContent({
          turns: [
            {
              role: "user",
              parts: [{ text: "Greet the user briefly." }],
            },
          ],
          turnComplete: true,
        });

        console.log("Gemini Live: Session started, initial greeting sent");
      } catch (err) {
        console.error("Error starting session:", err);
        setError(err.message);
        configRef.current.onError?.(err);
      } finally {
        setIsConnecting(false);
      }
    },
    [isSessionActive, isConnecting, handleGeminiMessage, getPlaybackContext]
  );

  /**
   * Send audio chunk to Gemini (via sendRealtimeInput)
   */
  const sendAudioToGemini = useCallback((int16Audio) => {
    if (!sessionRef.current) return;

    // Convert Int16Array to base64
    const bytes = new Uint8Array(int16Audio.buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(binary);

    // Send via sendRealtimeInput for low latency
    sessionRef.current.sendRealtimeInput({
      audio: {
        data: base64,
        mimeType: AUDIO_CONFIG.inputMimeType,
      },
    });
  }, []);

  /**
   * Start listening (capture microphone)
   */
  const startListening = useCallback(async () => {
    if (!isSessionActive) {
      console.log("startListening: Session not active, skipping");
      return;
    }

    // If already listening or already have a media stream, skip
    if (isListening || mediaStreamRef.current) {
      console.log("startListening: Already listening or stream exists, skipping");
      return;
    }

    try {
      console.log("startListening: Requesting microphone access...");
      // Get microphone stream
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // Double-check we haven't started listening while waiting for permission
      if (mediaStreamRef.current) {
        console.log("startListening: Stream was created while waiting, stopping new one");
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      mediaStreamRef.current = stream;

      // Create audio context for capture (16kHz)
      const captureContext = new AudioContext({ sampleRate: 16000 });
      captureContextRef.current = captureContext;
      const source = captureContext.createMediaStreamSource(stream);

      // Use ScriptProcessor for audio capture
      // Small buffer (2048) for lower latency
      const processor = captureContext.createScriptProcessor(2048, 1, 1);
      processorRef.current = processor;

      processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);

        // Convert Float32 to Int16
        const int16 = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          const s = Math.max(-1, Math.min(1, inputData[i]));
          int16[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }

        // Send directly to Gemini
        sendAudioToGemini(int16);
      };

      source.connect(processor);
      processor.connect(captureContext.destination);

      setIsListening(true);
      console.log("Gemini Live: Microphone capture started");
    } catch (err) {
      console.error("Error starting microphone:", err);
      setError("Microphone access denied");
    }
  }, [isSessionActive, isListening, sendAudioToGemini]);

  /**
   * Stop listening
   */
  const stopListening = useCallback(() => {
    console.log("stopListening called, isListening:", isListening);

    // Disconnect processor
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    // Stop microphone
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Close capture context
    if (captureContextRef.current) {
      try {
        captureContextRef.current.close();
      } catch (e) {
        // Context may already be closed
      }
      captureContextRef.current = null;
    }

    setIsListening(false);

    // Signal end of audio stream to Gemini
    if (sessionRef.current) {
      try {
        sessionRef.current.sendRealtimeInput({
          audioStreamEnd: true,
        });
        console.log("Gemini Live: Audio stream ended");
      } catch (err) {
        console.error("Error ending audio stream:", err);
      }
    }
  }, [isListening]);

  /**
   * Send text message
   */
  const sendText = useCallback(async (text) => {
    if (!sessionRef.current || !text.trim()) return;

    try {
      await sessionRef.current.sendClientContent({
        turns: [{ role: "user", parts: [{ text }] }],
        turnComplete: true,
      });
      setUserTranscript((prev) => prev + " " + text);
    } catch (err) {
      console.error("Error sending text:", err);
    }
  }, []);

  /**
   * Cleanup resources
   */
  const cleanup = useCallback(() => {
    // Disconnect processor
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }

    // Stop microphone
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Close capture context
    if (captureContextRef.current && captureContextRef.current.state !== "closed") {
      captureContextRef.current.close();
      captureContextRef.current = null;
    }

    // Close playback context
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Close Gemini session
    if (sessionRef.current) {
      try {
        sessionRef.current.close();
      } catch (e) {
        // Ignore close errors
      }
      sessionRef.current = null;
    }

    // Reset state
    nextPlayTimeRef.current = 0;
    setSessionId(null);
    setIsSessionActive(false);
    setIsListening(false);
    setIsSpeaking(false);
  }, []);

  /**
   * End session
   */
  const endSession = useCallback(async () => {
    cleanup();
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    isSessionActive,
    isConnecting,
    isListening,
    isSpeaking,
    error,
    sessionId,
    userTranscript,
    aiTranscript,
    startSession,
    endSession,
    startListening,
    stopListening,
    stopAllAudio,
    sendText,
  };
};
